import { isSafeVietnameseDisplayText } from "../presentation/text-guard";
import type {
  VietnameseDisplayFact,
  VietnameseMethodSection,
  VietnameseReportProjection,
} from "../presentation/types";

export type NarrativeSection = Readonly<{
  id: string;
  titleVi: string;
  paragraphsVi: readonly string[];
  citationIds: readonly string[];
}>;

// Every sentence below restates data already computed by the projection:
// fact label/value, the fact's own technical explanation, the section
// summary for blocked engines, warnings, and recommendation statuses.
// No fortune-telling, no advice beyond completing the profile.

function toSafeParagraph(text: string): string | undefined {
  const trimmed = text.trim();
  if (trimmed.length === 0 || !isSafeVietnameseDisplayText(trimmed)) {
    return undefined;
  }
  return trimmed;
}

function factSentence(fact: VietnameseDisplayFact): string {
  const base = `${fact.labelVi}: ${fact.valueVi}.`;
  return fact.explanationVi === undefined
    ? base
    : `${base} ${fact.explanationVi}`;
}

function buildExplanationParagraphs(
  methods: readonly VietnameseMethodSection[],
): readonly string[] {
  const paragraphs: string[] = [];
  for (const method of methods) {
    if (method.status === "sẵn_sàng" && method.facts.length > 0) {
      const sentences = method.facts
        .map(factSentence)
        .filter((sentence) => sentence.trim().length > 0)
        .join(" ");
      const paragraph = toSafeParagraph(`${method.titleVi}: ${sentences}`);
      if (paragraph !== undefined) {
        paragraphs.push(paragraph);
      }
      continue;
    }
    if (method.status !== "sẵn_sàng") {
      const paragraph = toSafeParagraph(
        `${method.titleVi}: ${method.summaryVi}`,
      );
      if (paragraph !== undefined) {
        paragraphs.push(paragraph);
      }
    }
  }
  return paragraphs;
}

function collectCitations(
  methods: readonly VietnameseMethodSection[],
): readonly string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const method of methods) {
    if (method.status !== "sẵn_sàng") {
      continue;
    }
    for (const fact of method.facts) {
      for (const citationId of fact.citationIds) {
        if (!seen.has(citationId)) {
          seen.add(citationId);
          ordered.push(citationId);
        }
      }
    }
  }
  return ordered;
}

function buildExplanationSection(
  projection: VietnameseReportProjection,
): NarrativeSection {
  return {
    id: "dien-giai-ky-thuat",
    titleVi: "Diễn giải kỹ thuật",
    paragraphsVi: buildExplanationParagraphs(projection.methods),
    citationIds: collectCitations(projection.methods),
  };
}

function buildPlanSection(
  projection: VietnameseReportProjection,
): NarrativeSection {
  const warnings: string[] = [];
  for (const method of projection.methods) {
    for (const warning of method.warningsVi) {
      if (!warnings.includes(warning)) {
        warnings.push(warning);
      }
    }
  }

  const thirtyDaySentences =
    warnings.length > 0
      ? warnings.map((warning) => ` ${warning}`)
      : [" hồ sơ hiện không có cảnh báo dữ kiện cần bổ sung."];
  const thirtyDay = toSafeParagraph(
    `Trong 30 ngày tới:${thirtyDaySentences.join("")}`,
  );

  const topics = projection.recommendations.topics;
  const ninetyDaySentences =
    topics.length > 0
      ? topics.map((topic) => ` ${topic.topicVi}: ${topic.statusVi}`)
      : [" không có hạng mục chờ bổ sung nào."];
  const ninetyDay = toSafeParagraph(
    `Trong 90 ngày tới:${ninetyDaySentences.join("")}`,
  );

  const coverage = toSafeParagraph(
    `${projection.recommendations.technicalSummaryVi} ${projection.recommendations.actionPlanStatusVi}`,
  );

  const paragraphs = [thirtyDay, ninetyDay, coverage].filter(
    (paragraph): paragraph is string => paragraph !== undefined,
  );

  return {
    id: "ke-hoach-30-90-ngay",
    titleVi: "Kế hoạch 30/90 ngày",
    paragraphsVi: paragraphs,
    citationIds: [],
  };
}

export function buildNarrative(
  projection: VietnameseReportProjection,
): readonly NarrativeSection[] {
  return [buildExplanationSection(projection), buildPlanSection(projection)];
}
